import React, { type CSSProperties, type ReactNode } from 'react'
import { clsx } from 'clsx'
import { Slot } from '@radix-ui/react-slot'
import type { ResponsiveLevel } from '../../theme/media'
import type { FontSizeProps } from '../../theme/typography'
import { Typography, type TypographyVariant } from '../Typography/Typography'
import { type Sprinkles, sprinkles } from '../../theme/sprinkles.css'

type TextStyleProps = {
  align?: Sprinkles['textAlign']
  balance?: boolean
  className?: string
  style?: CSSProperties
  color?: Sprinkles['color']
  singleLine?: boolean
  size?: FontSizeProps | 'inherit'
  strikethrough?: boolean
  uppercase?: boolean
}

export type TextProps = {
  id?: string
  as?: 'p' | 'div' | 'span' | 'sup' | 'legend'
  children?: ReactNode
  title?: string
  asChild?: boolean
} & TextStyleProps

// Map Text size to Typography variant
const mapSizeToVariant = (size: FontSizeProps | 'inherit'): string | Partial<Record<ResponsiveLevel, string>> => {
  if (size === 'inherit') return size

  const sizeMap: Record<string, string> = {
    xxs: 'standard.12',
    xs: 'standard.14',
    sm: 'standard.16',
    md: 'standard.18',
    lg: 'standard.20',
    xl: 'standard.24',
    xxl: 'standard.32',
    xxxl: 'standard.48',
  }

  // Handle string/number sizes
  if (typeof size === 'string' || typeof size === 'number') {
    return sizeMap[String(size)] || 'standard.18'
  }

  // Handle responsive object
  if (typeof size === 'object') {
    const responsiveVariant: Partial<Record<ResponsiveLevel, string>> = {}

    for (const [breakpoint, sizeValue] of Object.entries(size)) {
      if (sizeValue) {
        const mappedVariant = sizeMap[sizeValue] || 'standard.18'
        responsiveVariant[breakpoint as ResponsiveLevel] = mappedVariant
      }
    }

    return responsiveVariant
  }

  return 'standard.18'
}

export const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
  (
    {
      as = 'p',
      balance,
      align,
      color,
      children,
      singleLine,
      size = 'md',
      strikethrough,
      uppercase,
      className,
      asChild,
      ...rest
    }: TextProps,
    ref: React.ForwardedRef<HTMLParagraphElement>,
  ) => {
    const Component = asChild ? Slot : as
    const variant = mapSizeToVariant(size)

    return (
      <Typography
        ref={ref}
        variant={variant as TypographyVariant}
        align={align}
        balance={balance}
        singleLine={singleLine}
        strikethrough={strikethrough}
        uppercase={uppercase}
        className={clsx(className, sprinkles({ color }))}
        asChild
        {...rest}
      >
        <Component>{children}</Component>
      </Typography>
    )
  },
)

Text.displayName = 'Text'
